import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../i18n';

interface CommunicationDeliveryGridProps {
  metrics?: any;
  transcript?: any[];
}

const isAiTurn = (t: any): boolean => {
  if (!t) return false;
  const spk = String(t.speaker || '').toUpperCase();
  const role = String(t.role || '').toUpperCase();
  const spkName = String(t.speakerName || '').toUpperCase();
  if (spk === 'AI' || spk === 'AI_PERSONA' || spk.includes('AI') || spk.includes('CUSTOMER') || spk.includes('CALLER') || spk.includes('PERSONA') || role === 'ASSISTANT' || role === 'SYSTEM' || Boolean(t.ai)) {
    return true;
  }
  if (spkName.includes('AI') || spkName.includes('CUSTOMER') || spkName.includes('PERSONA') || spkName.includes('CALLER')) {
    return spk !== 'LEARNER' && role !== 'USER' && role !== 'LEARNER';
  }
  return false;
};

const isLearnerTurn = (t: any): boolean => {
  if (!t) return false;
  if (isAiTurn(t)) return false;
  const spk = String(t.speaker || '').toUpperCase();
  const role = String(t.role || '').toUpperCase();
  return spk === 'LEARNER' || role === 'USER' || role === 'LEARNER' || spk === 'YOU' || Boolean(t.learner) || (!t.speaker && !t.role);
};

const parseTurnSec = (t: any): number | null => {
  if (!t) return null;
  if (typeof t.timeOffsetSeconds === 'number' && !isNaN(t.timeOffsetSeconds)) return t.timeOffsetSeconds;
  if (typeof t.timestamp === 'number' && !isNaN(t.timestamp)) {
    return t.timestamp > 1e11 ? t.timestamp / 1000 : t.timestamp;
  }
  if (typeof t.timestamp === 'string' && t.timestamp.trim()) {
    const tm = t.timestamp.trim().match(/^(?:(\d+):)?(\d+):(\d+)$/);
    if (tm) {
      const h = tm[1] ? parseInt(tm[1], 10) : 0;
      return h * 3600 + parseInt(tm[2], 10) * 60 + parseInt(tm[3], 10);
    }
    const secMatch = t.timestamp.trim().match(/^(\d+(?:\.\d+)?)s?$/);
    if (secMatch) {
      return parseFloat(secMatch[1]);
    }
    const dt = new Date(t.timestamp).getTime();
    if (!isNaN(dt)) return dt / 1000;
  }
  return null;
};

const FILLER_PATTERNS = [
  { key: 'uh...ah', regex: /\bu+h+[\s\.\-]+a+h+\b/gi, label: 'uh...ah' },
  { key: 'hmmm', regex: /\bh+m{3,}\b/gi, label: 'hmmm' },
  { key: 'hmm', regex: /\bh+m{1,2}\b/gi, label: 'hmm' },
  { key: 'uhh', regex: /\bu+h{2,}\b/gi, label: 'uhh' },
  { key: 'uh', regex: /\bu+h\b/gi, label: 'uh' },
  { key: 'umm', regex: /\bu+m{2,}\b|\bu+h+m{2,}\b/gi, label: 'umm' },
  { key: 'um', regex: /\bu+m\b|\bu+h+m\b/gi, label: 'um' },
  { key: 'ah', regex: /\ba+h+\b/gi, label: 'ah' },
  { key: 'er', regex: /\be+r+m?\b/gi, label: 'er' },
  { key: 'mmm', regex: /\bm{2,}\b/gi, label: 'mmm' },
  { key: 'उम', regex: /(?:^|\s)उ+[मम्]+(?:$|\s|[.,!?])/gu, label: 'उम' },
  { key: 'हम्म', regex: /(?:^|\s)ह+[मम्]+(?:$|\s|[.,!?])/gu, label: 'हम्म' },
  { key: 'मम्म', regex: /(?:^|\s)म+[मम्]+(?:$|\s|[.,!?])/gu, label: 'मम्म' },
  { key: 'अह', regex: /(?:^|\s)अ+ह+(?:$|\s|[.,!?])/gu, label: 'अह' },
  { key: 'आह', regex: /(?:^|\s)आ+ह+(?:$|\s|[.,!?])/gu, label: 'आह' },
  { key: 'मतलब', regex: /(?:^|\s)मतलब(?:$|\s|[.,!?])/gu, label: 'मतलब' },
  { key: 'तो', regex: /(?:^|\s)तो(?:$|\s|[.,!?])/gu, label: 'तो' },
  { key: 'जैसे कि', regex: /(?:^|\s)जैसे\s+कि(?:$|\s|[.,!?])/gu, label: 'जैसे कि' },
  { key: 'like', regex: /\blike\b/gi, label: 'like' },
  { key: 'actually', regex: /\bactually\b/gi, label: 'actually' },
  { key: 'basically', regex: /\bbasically\b/gi, label: 'basically' },
  { key: 'you know', regex: /\byou\s+know\b/gi, label: 'you know' },
  { key: 'sort of', regex: /\bsort\s+of\b/gi, label: 'sort of' },
  { key: 'kind of', regex: /\bkind\s+of\b/gi, label: 'kind of' },
  { key: 'i mean', regex: /\bi\s+mean\b/gi, label: 'i mean' },
  { key: 'literally', regex: /\bliterally\b/gi, label: 'literally' },
  { key: 'honestly', regex: /\bhonestly\b/gi, label: 'honestly' },
  { key: 'so...', regex: /(?:^|[.!?]\s+)\bso\b/gi, label: 'so...' },
  { key: 'well...', regex: /(?:^|[.!?]\s+)\bwell\b/gi, label: 'well...' },
  { key: 'matlab', regex: /\bmatlab\b/gi, label: 'matlab' },
  { key: 'toh', regex: /\btoh\b/gi, label: 'toh' },
  { key: 'jaise ki', regex: /\bjaise\s+ki\b/gi, label: 'jaise ki' }
];

export const CommunicationDeliveryGrid: React.FC<CommunicationDeliveryGridProps> = ({ metrics, transcript = [] }) => {
  const { t } = useTranslation();
  // Dynamic fallback from transcript if backend metrics are missing or uncalculated
  const derivedMetrics = React.useMemo(() => {
    if (!transcript || !Array.isArray(transcript) || transcript.length === 0) return null;

    let totalLearnerWords = 0;
    let totalCustomerWords = 0;
    let learnerDurationSec = 0;
    let totalVerbalFillers = 0;
    const fillerMap: Record<string, number> = {};
    let briefPauses = 0;
    let extendedPauses = 0;
    let respSum = 0;
    let respSamples = 0;

    transcript.forEach((t, idx) => {
      if (!t) return;
      const isCustomer = isAiTurn(t);
      const text = String(t.text || t.content || t.message || '').trim();
      const words = text ? text.split(/\s+/).filter(Boolean).length : 0;

      if (isCustomer) {
        totalCustomerWords += words;
      } else {
        totalLearnerWords += words;
        if (typeof t.durationSeconds === 'number' && t.durationSeconds > 0) {
          learnerDurationSec += t.durationSeconds;
        } else if (typeof t.durationMs === 'number' && t.durationMs > 0) {
          learnerDurationSec += t.durationMs / 1000;
        } else if (words > 0) {
          learnerDurationSec += Math.max(1.5, (words / 2.3) + 0.4);
        }

        // Verbal filler detection in learner speech
        if (text) {
          FILLER_PATTERNS.forEach(fp => {
            const m = text.match(fp.regex);
            if (m && m.length > 0) {
              totalVerbalFillers += m.length;
              fillerMap[fp.label] = (fillerMap[fp.label] || 0) + m.length;
            }
          });

          // Hesitation punctuation / pauses written into text
          const ellipsisMatches = text.match(/(?:\.{2,}|\(pause\)|\[pause\]|\(silence\)|--)/gi);
          if (ellipsisMatches && ellipsisMatches.length > 0) {
            briefPauses += ellipsisMatches.length;
          }
        }

        // Latency after customer turn
        if (idx > 0) {
          const prev = transcript[idx - 1];
          if (prev && isAiTurn(prev)) {
            // Microphone-measured hesitation (persona finished → learner started) wins over
            // wall-clock turn timestamps when the live voice runtime captured it.
            const measured = Number(t.speechMetrics?.hesitationMs ?? t.hesitationMs ?? 0) / 1000;
            const prevSec = parseTurnSec(prev);
            const currSec = parseTurnSec(t);
            let latency: number | null = null;
            if (measured > 0 && measured < 30) {
              latency = measured;
            } else if (prevSec !== null && currSec !== null && currSec >= prevSec) {
              const prevDur = prev.durationSeconds || (prev.durationMs ? prev.durationMs / 1000 : 0) || 0;
              latency = (prevDur > 0 && currSec >= prevSec + prevDur) ? (currSec - (prevSec + prevDur)) : (currSec - prevSec);
            }
            if (latency !== null && latency > 0.05 && latency < 30) {
              respSum += latency;
              respSamples++;
            }
            if (latency !== null) {
              if (latency >= 3.5 && latency <= 25.0) {
                extendedPauses++;
              } else if (latency >= 1.8 && latency < 3.5) {
                briefPauses++;
              }
            }
          }
        }
      }
    });

    const totalWords = totalLearnerWords + totalCustomerWords;
    if (totalLearnerWords === 0) return null;

    const minutes = Math.max(0.05, learnerDurationSec / 60);
    const rawWpm = Math.round(totalLearnerWords / minutes);
    const wpm = Math.max(85, Math.min(210, rawWpm));
    const learnerPct = totalWords > 0 ? Math.round((totalLearnerWords / totalWords) * 100) : 50;
    const customerPct = 100 - learnerPct;

    const derivedFillerItems: Array<{ word: string; count: number }> = [];
    Object.entries(fillerMap).forEach(([word, count]) => {
      derivedFillerItems.push({ word, count });
    });
    if (extendedPauses > 0) {
      derivedFillerItems.push({ word: 'long pause (...)', count: extendedPauses });
    }
    if (briefPauses > 0) {
      derivedFillerItems.push({ word: 'pause (...)', count: briefPauses });
    }

    const totalPauses = briefPauses + extendedPauses;
    const combinedFillers = totalVerbalFillers + totalPauses;
    const fillersPerMin = Number((combinedFillers / Math.max(0.1, minutes)).toFixed(1));

    return {
      wpm,
      learnerPct,
      customerPct,
      totalLearnerWords,
      totalCustomerWords,
      fillerItems: derivedFillerItems,
      verbalCount: totalVerbalFillers,
      pauseCount: totalPauses,
      fillerCount: combinedFillers,
      fillersPerMin,
      avgResponseSec: respSamples > 0 ? Number((respSum / respSamples).toFixed(1)) : null,
      responseSamples: respSamples
    };
  }, [transcript]);

  // 1. Speaking Pace
  const rawPaceWpm = metrics?.speakingPace?.wpm ?? metrics?.pace?.wpm ?? metrics?.speakingPaceWpm ?? metrics?.wpm;
  const paceWpm = (typeof rawPaceWpm === 'number' && rawPaceWpm > 0)
    ? rawPaceWpm
    : (derivedMetrics?.wpm ?? (rawPaceWpm ?? 0));

  const paceStatus = metrics?.speakingPace?.status || (paceWpm >= 125 && paceWpm <= 165 ? 'Good' : (paceWpm === 0 ? 'N/A' : 'Watch'));
  const paceNote = metrics?.speakingPace?.note || (paceWpm > 165
    ? `Speaking pace was fast (${paceWpm} wpm). Aim for 130–160 wpm to enhance customer comprehension and clarity.`
    : (paceWpm < 125 && paceWpm > 0
      ? `Speaking pace was deliberate/slow (${paceWpm} wpm). Aim for 130–160 wpm for dynamic, engaging delivery.`
      : (paceWpm === 0 ? 'No learner speech recorded during this attempt.' : 'Steady, conversational pace maintained throughout (130–160 wpm ideal).')));

  // 2. Filler Words & Pauses
  const backendItems = metrics?.fillerWords?.items || metrics?.clarity?.fillerWords;
  const backendHasVerbal = backendItems && backendItems.some((it: any) => it.word && !it.word.toLowerCase().includes('pause'));
  const derivedHasVerbal = derivedMetrics?.fillerItems && derivedMetrics.fillerItems.some((it: any) => it.word && !it.word.toLowerCase().includes('pause'));

  let fillerItems: Array<{ word?: string; phrase?: string; count?: number }> = [];
  if (backendItems && backendItems.length > 0) {
    if (!backendHasVerbal && derivedHasVerbal) {
      // Merge: preserve pause items from backend and include detected verbal fillers from transcript
      const pauseItems = backendItems.filter((it: any) => it.word && it.word.toLowerCase().includes('pause'));
      const verbalItems = (derivedMetrics?.fillerItems || []).filter((it: any) => it.word && !it.word.toLowerCase().includes('pause'));
      fillerItems = [...verbalItems, ...pauseItems];
    } else {
      fillerItems = backendItems;
    }
  } else {
    fillerItems = derivedMetrics?.fillerItems || [];
  }

  const verbalCount = metrics?.fillerWords?.verbalTotal ?? (fillerItems.filter((it: any) => it.word && !it.word.toLowerCase().includes('pause')).reduce((acc, it) => acc + (it.count || 1), 0));
  const pauseCount = metrics?.pauses?.total ?? metrics?.fillerWords?.pauseTotal ?? (derivedMetrics?.pauseCount ?? 0);
  const fillerCount = metrics?.fillerWords?.combinedTotal ?? (fillerItems.length > 0 ? fillerItems.reduce((acc, it) => acc + (it.count || 1), 0) : (verbalCount + pauseCount));
  const fillersPerMin = metrics?.fillerWords?.perMinute ?? (derivedMetrics?.fillersPerMin ?? (typeof metrics?.fillersPerMin === 'number' ? metrics.fillersPerMin : 0));
  const fillerStatus = metrics?.fillerWords?.status || (fillerCount <= 4 ? 'Good' : 'Watch');
  const fillerNote = fillerCount === 0
    ? 'Clear, articulate delivery with natural pacing and zero filler habits.'
    : (fillerCount <= 4
      ? 'Well-controlled delivery with minimal hesitation pauses or verbal fillers.'
      : 'Frequent hesitation pauses or filler words detected. Practice deliberate, silent pauses.');

  // 3. Repeated Words
  const repeatedItems: Array<{ phrase?: string; word?: string; count?: number }> =
    metrics?.repeatedPhrases?.items || metrics?.repeatedWords?.items || [];
  const repeatedCount = metrics?.repeatedPhrases?.count ?? metrics?.repeatedWords?.count ?? repeatedItems.length;
  const repeatedStatus = metrics?.repeatedPhrases?.status || (repeatedCount <= 1 ? 'Good' : 'Watch');
  const repeatedNote = repeatedCount === 0
    ? 'Diverse vocabulary with no repetitive patterns.'
    : 'Vary your language. Try alternating repetitive phrases with specific situation acknowledgements.';

  // 4. Talk-to-Listen Ratio
  const rawLearnerRatio = metrics?.talkListenRatio?.learnerPct ?? (typeof metrics?.learnerTalkRatio === 'number' ? metrics.learnerTalkRatio : undefined);
  const learnerTalkRatio = rawLearnerRatio !== undefined
    ? rawLearnerRatio
    : (derivedMetrics?.learnerPct ?? (metrics?.status === 'UNAVAILABLE' || paceWpm === 0 ? 0 : 50));
  const customerRatio = metrics?.talkListenRatio?.aiPct ?? (derivedMetrics?.customerPct ?? (100 - learnerTalkRatio));
  const ratioStatus = metrics?.talkListenRatio?.status || (learnerTalkRatio >= 38 && learnerTalkRatio <= 58 ? 'Good' : (learnerTalkRatio === 0 ? 'N/A' : 'Watch'));
  const ratioNote = metrics?.talkListenRatio?.note || (learnerTalkRatio > 58
    ? `You spoke majority of the time (${learnerTalkRatio}% you / ${customerRatio}% customer). In customer interactions, allow more room for the customer to explain.`
    : (learnerTalkRatio < 38 && learnerTalkRatio > 0
      ? `Customer spoke majority of the time (${customerRatio}% customer / ${learnerTalkRatio}% you). Proactively guide the conversation and summarize key points.`
      : (learnerTalkRatio === 0
        ? 'No learner speech recorded during this attempt.'
        : `Balanced conversational turn-taking (${learnerTalkRatio}% you / ${customerRatio}% customer). Excellent active listening.`)));

  // 5. Interruptions
  const interruptions = metrics?.interruptions?.count ?? 0;
  const interruptionStatus = metrics?.interruptions?.status || (interruptions === 0 ? 'Good' : 'Watch');
  const interruptionNote = interruptions === 0
    ? 'Patient active listening and turn-taking maintained.'
    : `${interruptions} speech overlap(s) detected. Practice waiting for customer to finish speaking.`;

  // 6. Longest Monologue
  const monologueSec = metrics?.longestMonologue?.seconds ?? metrics?.longestMonologueSeconds ?? 0;
  const monologueStatus = metrics?.longestMonologue?.status || (monologueSec <= 45 ? 'Good' : 'Watch');
  const monologueNote = monologueSec <= 45
    ? 'Concise turns without overwhelming the customer.'
    : 'Long turns detected. Break explanations into digestible checkpoints.';

  // 7. Response Time — only when the attempt actually produced turn-transition samples.
  // The backend value is authoritative only when it shipped with its sample count (current
  // analyzer shape); older persisted results are recomputed from the transcript so a legacy
  // placeholder can never outvote the real turn timing.
  const backendResponseSamples = typeof metrics?.responseTime?.samples === 'number';
  const rawResponseSec = metrics?.responseTime?.seconds ?? metrics?.averageResponseTimeSeconds ?? null;
  const backendResponseSec: number | null = typeof rawResponseSec === 'number' && rawResponseSec > 0 ? rawResponseSec : null;
  const responseValue: number | null = backendResponseSamples
    ? backendResponseSec
    : (derivedMetrics?.avgResponseSec ?? backendResponseSec);
  const responseMeasured = responseValue !== null;
  const responseTime = responseMeasured ? (responseValue as number) : null;
  const responseStatus = responseMeasured
    ? (backendResponseSamples && metrics?.responseTime?.status
      ? metrics.responseTime.status
      : (responseValue! <= 2.5 ? 'Good' : 'Watch'))
    : 'Not Applicable';
  const responseNote = responseMeasured
    ? (metrics?.responseTime?.note || (responseValue! <= 2.5
      ? 'Prompt response time without awkward latency.'
      : 'Noticeable response delays detected. Acknowledge customer promptly.'))
    : (metrics?.responseTime?.note || 'No learner turn timing was captured for this attempt, so response latency could not be measured.');

  // 8. Questions Asked
  const questionsCount = metrics?.questions?.total ?? metrics?.questionsAsked?.total ?? metrics?.questionsCount ?? 0;
  const openQuestions = metrics?.questions?.openCount ?? metrics?.openQuestions ?? questionsCount;
  const closedQuestions = metrics?.questions?.closedCount ?? metrics?.closedQuestions ?? 0;
  const questionStatus = metrics?.questions?.status || (questionsCount >= 2 ? 'Good' : (questionsCount >= 1 ? 'Watch' : 'Watch'));
  const questionNote = questionsCount >= 2
    ? 'Open discovery questions helped clarify context effectively.'
    : (questionsCount === 1
      ? 'Only 1 question asked. Ask more probing questions to uncover customer needs.'
      : 'No discovery questions asked during this interaction.');

  // 9. Jargon Used
  const jargonTerms: string[] = metrics?.jargon?.terms ?? metrics?.jargonTerms ?? [];
  const jargonCount = metrics?.jargon?.count ?? jargonTerms.length;
  const jargonStatus = metrics?.jargon?.status || (jargonCount <= 1 ? 'Good' : 'Watch');
  const jargonNote = jargonCount === 0
    ? 'Spoke in clear, customer-accessible language throughout.'
    : `${jargonCount} technical term(s) detected. Ensure customer is comfortable with domain jargon.`;

  const renderBadge = (status: string) => {
    const isGood = status === 'Good' || status === 'Ideal';
    const isWatch = status === 'Watch';
    const bg = isGood ? '#D1FAE5' : (isWatch ? '#FEF3C7' : '#F3F4F6');
    const fg = isGood ? '#047857' : (isWatch ? '#B45309' : '#6B7280');
    const displayStatus = isGood ? t('scorecard.status_good', undefined, status) : (isWatch ? t('scorecard.status_watch', undefined, 'Watch') : t('scorecard.status_needs_work', undefined, status));
    return (
      <span style={{ fontSize: '9.5px', fontWeight: 700, background: bg, color: fg, padding: '1px 6px', borderRadius: '4px' }}>
        {displayStatus}
      </span>
    );
  };

  interface EvidenceModalData {
    title: string;
    badgeLabel: string;
    badgeType: 'filler' | 'repeated' | 'interruption' | 'jargon';
    term?: string;
    count: number;
    description: string;
    turns: Array<{
      turnIndex: number;
      speaker: string;
      isLearner: boolean;
      timestamp?: string;
      text: string;
      matchedSnippet?: React.ReactNode;
    }>;
  }

  const [activeEvidence, setActiveEvidence] = useState<EvidenceModalData | null>(null);

  useEffect(() => {
    if (!activeEvidence) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveEvidence(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeEvidence]);

  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const openEvidenceModal = (
    type: 'filler' | 'repeated' | 'interruption' | 'jargon',
    term?: string,
    count = 1
  ) => {
    const turns = transcript || [];
    const foundTurns: Array<{
      turnIndex: number;
      speaker: string;
      isLearner: boolean;
      timestamp?: string;
      text: string;
      matchedSnippet?: React.ReactNode;
    }> = [];

    const formatTs = (raw: any, fallbackIdx: number) => {
      if (typeof raw === 'number') {
        const m = Math.floor(raw / 60);
        const s = Math.floor(raw % 60);
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      }
      if (typeof raw === 'string' && raw.trim()) return raw;
      return `Turn ${fallbackIdx + 1}`;
    };

    if (type === 'filler') {
      const searchTerm = (term || '').trim();
      const isPauseSearch = searchTerm?.toLowerCase().includes('pause');
      turns.forEach((t, idx) => {
        const isLearner = isLearnerTurn(t);
        const text = t.text || t.content || t.message || '';
        if (!text) return;

        let isMatch = false;
        let highlighted: any = text;

        if (isPauseSearch) {
          const pauseRegex = /(?:\.{2,}|\(pause\)|\[pause\]|\(silence\)|--)/gi;
          if (pauseRegex.test(text)) {
            isMatch = true;
            highlighted = text.split(pauseRegex).map((part: string, pIdx: number) => (
              pauseRegex.test(part) ? (
                <mark key={pIdx} style={{ background: '#FEF08A', color: '#854D0E', padding: '1px 5px', borderRadius: '3px', fontWeight: 700 }}>
                  {part}
                </mark>
              ) : part
            ));
          } else if (idx > 0 && isLearner) {
            const prev = turns[idx - 1];
            if (prev) {
              const prevSec = parseTurnSec(prev);
              const currSec = parseTurnSec(t);
              if (prevSec !== null && currSec !== null && currSec >= prevSec) {
                const prevDur = prev.durationSeconds || (prev.durationMs ? prev.durationMs / 1000 : 0) || 0;
                const diffSec = (prevDur > 0 && currSec >= prevSec + prevDur) ? (currSec - (prevSec + prevDur)) : (currSec - prevSec);
                if (diffSec >= 1.8) {
                  isMatch = true;
                  highlighted = (
                    <span>
                      <span style={{ background: '#FEF08A', color: '#854D0E', padding: '1px 5px', borderRadius: '3px', fontWeight: 700, marginRight: '6px' }}>
                        ⏱️ {diffSec.toFixed(1)}s Pause before turn
                      </span>
                      {text}
                    </span>
                  );
                }
              }
            }
          }
        } else {
          const searchTermLower = (searchTerm || '').toLowerCase().trim();
          const splitRegex = searchTermLower
            ? (searchTermLower === 'uh' ? /(\bu+h\b)/gi
              : searchTermLower === 'uhh' ? /(\bu+h{2,}\b)/gi
              : searchTermLower === 'hmm' ? /(\bh+m{1,2}\b)/gi
              : searchTermLower === 'hmmm' ? /(\bh+m{3,}\b)/gi
              : searchTermLower === 'um' ? /(\bu+m+\b|\bu+h+m+\b)/gi
              : new RegExp(`(\\b${escapeRegex(searchTermLower)}\\b)`, 'gi'))
            : /(\b(?:uh|uhh|um|umm|er|ah|hmm|hmmm|mmm|like|you know|actually|basically|literally|honestly|so|well)\b|(?:\.{2,}|\(pause\)|\[pause\]))/gi;

          const testRegex = searchTermLower
            ? (searchTermLower === 'uh' ? /\bu+h\b/i
              : searchTermLower === 'uhh' ? /\bu+h{2,}\b/i
              : searchTermLower === 'hmm' ? /\bh+m{1,2}\b/i
              : searchTermLower === 'hmmm' ? /\bh+m{3,}\b/i
              : searchTermLower === 'um' ? /\bu+m+|\bu+h+m+/i
              : new RegExp(`\\b${escapeRegex(searchTermLower)}\\b`, 'i'))
            : /\b(?:uh|uhh|um|umm|er|ah|hmm|hmmm|mmm|like|you know|actually|basically|literally|honestly|so|well)\b|(?:\.{2,}|\(pause\)|\[pause\])/i;

          if (testRegex.test(text)) {
            isMatch = true;
            const parts: string[] = text.split(splitRegex);
            highlighted = parts.map((part: string, pIdx: number) => {
              const matches = testRegex.test(part);
              if (matches) {
                return (
                  <mark key={pIdx} style={{ background: '#FEF08A', color: '#854D0E', padding: '1px 5px', borderRadius: '3px', fontWeight: 700 }}>
                    {part}
                  </mark>
                );
              }
              return part;
            });
          }
        }

        if (isMatch) {
          foundTurns.push({
            turnIndex: t.turnNumber ?? idx + 1,
            speaker: isLearner ? 'You (Learner)' : 'Customer',
            isLearner,
            timestamp: formatTs(t.timestamp, idx),
            text,
            matchedSnippet: highlighted,
          });
        }
      });

      setActiveEvidence({
        title: searchTerm ? `Filler Word: "${searchTerm}"` : 'Filler Words & Pauses',
        badgeLabel: `${count} occurrence${count === 1 ? '' : 's'}`,
        badgeType: 'filler',
        term: searchTerm,
        count,
        description: searchTerm
          ? `Review transcript dialogue turns where "${searchTerm}" was detected.`
          : 'Review transcript dialogue where hesitation pauses or filler words were detected.',
        turns: foundTurns.length > 0 ? foundTurns : turns.slice(0, 3).map((t, idx) => ({
          turnIndex: t.turnNumber ?? idx + 1,
          speaker: isLearnerTurn(t) ? 'You (Learner)' : 'Customer',
          isLearner: isLearnerTurn(t),
          timestamp: formatTs(t.timestamp, idx),
          text: t.text || t.content || '',
          matchedSnippet: t.text || t.content || '',
        })),
      });
    } else if (type === 'repeated') {
      const searchTerm = (term || '').trim();
      turns.forEach((t, idx) => {
        const isLearner = isLearnerTurn(t);
        const text = t.text || t.content || t.message || '';
        if (!text) return;

        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        if (regex.test(text)) {
          const parts: string[] = text.split(regex);
          const highlighted = parts.map((part: string, pIdx: number) => {
            if (part.toLowerCase() === searchTerm.toLowerCase()) {
              return (
                <mark
                  key={pIdx}
                  style={{
                    background: '#FED7AA',
                    color: '#9A3412',
                    padding: '1px 5px',
                    borderRadius: '3px',
                    fontWeight: 700,
                  }}
                >
                  {part}
                </mark>
              );
            }
            return part;
          });

          foundTurns.push({
            turnIndex: t.turnNumber ?? idx + 1,
            speaker: isLearner ? 'You (Learner)' : 'Customer',
            isLearner,
            timestamp: formatTs(t.timestamp, idx),
            text,
            matchedSnippet: highlighted,
          });
        }
      });

      setActiveEvidence({
        title: `Repeated Phrase: "${searchTerm}"`,
        badgeLabel: `${count} times found`,
        badgeType: 'repeated',
        term: searchTerm,
        count,
        description: `Review transcript dialogue turns where "${searchTerm}" was repeated in quick succession.`,
        turns: foundTurns.length > 0 ? foundTurns : turns.slice(0, 3).map((t, idx) => ({
          turnIndex: t.turnNumber ?? idx + 1,
          speaker: isLearnerTurn(t) ? 'You (Learner)' : 'Customer',
          isLearner: isLearnerTurn(t),
          timestamp: formatTs(t.timestamp, idx),
          text: t.text || t.content || '',
          matchedSnippet: t.text || t.content || '',
        })),
      });
    } else if (type === 'interruption') {
      turns.forEach((t, idx) => {
        const isLearner = isLearnerTurn(t);
        const text = t.text || t.content || t.message || '';
        // Only trust the events captured by the live voice runtime — inferring an overlap from
        // a short reply latency would flag every fast answer as an interruption.
        const turnEvents: any[] = [
          ...(Array.isArray((t as any).interruptions) ? (t as any).interruptions : []),
          ...(Array.isArray((t as any).interruptionEvents) ? (t as any).interruptionEvents : []),
          ...((t as any).interruption ? [(t as any).interruption] : []),
        ].filter((ev) => ev && typeof ev === 'object');
        const isOverlap = turnEvents.length > 0
          || (t as any).speechOverlap === true
          || (t as any).isInterrupted === true;

        if (isOverlap || (foundTurns.length === 0 && isLearner && text.length > 0)) {
          const longestMs = turnEvents.reduce((max, ev) => Math.max(max, Number(ev.durationMs) || 0), 0);
          const interruptedWho = turnEvents.find((ev) => ev.interruptedSpeaker || ev.interruptedRole)?.interruptedSpeaker;
          foundTurns.push({
            turnIndex: t.turnNumber ?? idx + 1,
            speaker: isLearner ? 'You (Learner)' : 'Customer',
            isLearner,
            timestamp: formatTs(t.timestamp, idx),
            text,
            matchedSnippet: (
              <span>
                <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '1px 6px', borderRadius: '3px', fontWeight: 700, fontSize: '11px', marginRight: '6px' }}>
                  {'[Speech Overlap]'}{longestMs > 0 ? ` ${Math.round(longestMs / 100) / 10}s` : ''}{interruptedWho ? ` vs ${interruptedWho}` : ''}
                </span>
                {text}
              </span>
            ),
          });
        }
      });

      setActiveEvidence({
        title: 'Speech Overlaps & Interruptions',
        badgeLabel: `${count} detected`,
        badgeType: 'interruption',
        count,
        description: 'Review exchanges where turns overlapped or cut in before the customer completed their explanation.',
        turns: foundTurns.length > 0 ? foundTurns : turns.slice(0, 3).map((t, idx) => ({
          turnIndex: t.turnNumber ?? idx + 1,
          speaker: isAiTurn(t) ? 'Customer' : 'You (Learner)',
          isLearner: !isAiTurn(t),
          timestamp: formatTs(t.timestamp, idx),
          text: t.text || t.content || '',
          matchedSnippet: t.text || t.content || '',
        })),
      });
    } else if (type === 'jargon') {
      const searchTerm = (term || '').trim();
      turns.forEach((t, idx) => {
        const isLearner = isLearnerTurn(t);
        const text = t.text || t.content || t.message || '';
        if (!text) return;

        const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
        if (regex.test(text)) {
          const parts: string[] = text.split(regex);
          const highlighted = parts.map((part: string, pIdx: number) => {
            if (part.toLowerCase() === searchTerm.toLowerCase()) {
              return (
                <mark
                  key={pIdx}
                  style={{
                    background: '#FEF3C7',
                    color: '#B45309',
                    padding: '1px 5px',
                    borderRadius: '3px',
                    fontWeight: 700,
                  }}
                >
                  {part}
                </mark>
              );
            }
            return part;
          });

          foundTurns.push({
            turnIndex: t.turnNumber ?? idx + 1,
            speaker: isLearner ? 'You (Learner)' : 'Customer',
            isLearner,
            timestamp: formatTs(t.timestamp, idx),
            text,
            matchedSnippet: highlighted,
          });
        }
      });

      setActiveEvidence({
        title: `Technical Jargon: "${searchTerm}"`,
        badgeLabel: `${count} occurrence${count === 1 ? '' : 's'}`,
        badgeType: 'jargon',
        term: searchTerm,
        count,
        description: `Review where "${searchTerm}" was used. Confirm customer familiarity with this specialized term.`,
        turns: foundTurns.length > 0 ? foundTurns : turns.slice(0, 3).map((t, idx) => ({
          turnIndex: t.turnNumber ?? idx + 1,
          speaker: isLearnerTurn(t) ? 'You (Learner)' : 'Customer',
          isLearner: isLearnerTurn(t),
          timestamp: formatTs(t.timestamp, idx),
          text: t.text || t.content || '',
          matchedSnippet: t.text || t.content || '',
        })),
      });
    }
  };

  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 22px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '16px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header with Microphone SVG Icon */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#E8F0FE',
              color: '#0D4FBD',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
            </svg>
          </span>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '15.5px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.comm_delivery_title', undefined, 'Communication & delivery')}
          </h2>
        </div>

        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {t('scorecard.from_voice_and_transcript', undefined, 'From your voice and transcript')}
        </span>
      </div>

      {/* 3x3 Grid of Tiles */}
      <div className="comm-delivery-grid">
        {/* Tile 1: Speaking Pace */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>{t('scorecard.speaking_pace', undefined, 'Speaking pace')}</span>
            {renderBadge(paceStatus)}
          </div>

          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--navy)' }}>
            {paceWpm} <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>{t('scorecard.words_per_min', undefined, 'words/min')}</span>
          </div>

          {/* Pace Gauge Bar */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: 'var(--muted)', marginBottom: '3px' }}>
              <span>100</span>
              <span style={{ fontWeight: 700, color: '#10B981' }}>{t('scorecard.ideal_pace', undefined, '130 - 160 Ideal')}</span>
              <span>200</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: '#E5E7EB', borderRadius: '4px', position: 'relative' }}>
              <div style={{ position: 'absolute', left: '30%', width: '30%', height: '100%', background: '#A7F3D0', borderRadius: '2px' }} />
              <div
                style={{
                  position: 'absolute',
                  left: `${paceWpm > 0 ? Math.min(94, Math.max(6, ((Math.max(100, Math.min(200, paceWpm)) - 100) / 100) * 100)) : 6}%`,
                  top: '-2px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: paceStatus === 'Good' ? '#10B981' : (paceWpm === 0 ? '#9CA3AF' : '#F59E0B'),
                  border: '1.5px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  transition: 'left 0.3s ease'
                }}
              />
            </div>
          </div>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {paceNote}
          </p>
        </div>

        {/* Tile 2: Filler Words */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>{t('scorecard.filler_words_title', undefined, 'Filler words & pauses')}</span>
            {renderBadge(fillerStatus)}
          </div>

          <div
            onClick={() => fillerCount > 0 && openEvidenceModal('filler', undefined, fillerCount)}
            style={{
              fontSize: '18px',
              fontWeight: 800,
              color: 'var(--navy)',
              cursor: fillerCount > 0 ? 'pointer' : 'default',
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '6px',
            }}
            title={fillerCount > 0 ? 'Click to inspect all filler words and pauses in transcript' : undefined}
          >
            <span>{fillerCount}</span>
            <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>
              total · {fillersPerMin}/min {pauseCount > 0 ? `(${pauseCount} pause${pauseCount > 1 ? 's' : ''})` : ''} {fillerCount > 0 && <span style={{ color: 'var(--blue)', fontSize: '10px' }}>🔍 View</span>}
            </span>
          </div>

          {/* Filler Word / Hesitation Pause Breakdown Chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', minHeight: '22px' }}>
            {fillerItems && fillerItems.length > 0 ? (
              fillerItems.map((item, idx) => {
                const word = item.word || item.phrase || 'filler';
                const count = item.count || 1;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => openEvidenceModal('filler', word, count)}
                    style={{
                      border: '1px solid var(--line)',
                      background: 'var(--white)',
                      color: 'var(--navy)',
                      padding: '2px 7px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--blue)';
                      e.currentTarget.style.color = 'var(--blue)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--line)';
                      e.currentTarget.style.color = 'var(--navy)';
                    }}
                    title={`Click to view "${word}" in transcript dialogue`}
                  >
                    <span>"{word}"</span>
                    <span style={{ background: '#E8F0FE', color: 'var(--blue)', padding: '1px 4px', borderRadius: '3px', fontSize: '9px', fontWeight: 700 }}>
                      x{count}
                    </span>
                  </button>
                );
              })
            ) : (
              <span
                style={{ fontSize: '10.5px', color: '#047857', fontWeight: 600 }}
                title="No verbal fillers (e.g., um, like, actually, basically) or response pauses detected in this transcript."
              >
                Zero hesitation pauses or fillers detected ✓
              </span>
            )}
          </div>

          {fillerItems && fillerItems.length > 0 && (
            <span style={{ fontSize: '9.5px', color: 'var(--blue)', fontWeight: 500 }}>
              👆 Click any word to see transcript dialogue
            </span>
          )}

          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            Aim for under 3 per minute
          </span>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {fillerNote}
          </p>
        </div>

        {/* Tile 3: Repeated Words */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>Repeated words</span>
            {renderBadge(repeatedStatus)}
          </div>

          <div
            onClick={() => repeatedCount > 0 && repeatedItems[0] && openEvidenceModal('repeated', repeatedItems[0].phrase || repeatedItems[0].word, repeatedCount)}
            style={{
              fontSize: '18px',
              fontWeight: 800,
              color: 'var(--navy)',
              cursor: repeatedCount > 0 ? 'pointer' : 'default',
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '6px',
            }}
            title={repeatedCount > 0 ? 'Click to inspect repeated phrases in transcript' : undefined}
          >
            <span>{repeatedCount}</span>
            <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>
              phrases repeated {repeatedCount > 0 && <span style={{ color: 'var(--blue)', fontSize: '10px' }}>🔍 View</span>}
            </span>
          </div>

          {/* Repeated Phrase Chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', minHeight: '22px' }}>
            {repeatedItems && repeatedItems.length > 0 ? (
              repeatedItems.map((item, idx) => {
                const phrase = item.phrase || item.word || 'phrase';
                const count = item.count || 2;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => openEvidenceModal('repeated', phrase, count)}
                    style={{
                      border: '1px solid var(--line)',
                      background: 'var(--white)',
                      color: 'var(--navy)',
                      padding: '2px 7px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--blue)';
                      e.currentTarget.style.color = 'var(--blue)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--line)';
                      e.currentTarget.style.color = 'var(--navy)';
                    }}
                    title={`Click to view "${phrase}" in transcript dialogue`}
                  >
                    <span>"{phrase}"</span>
                    <span style={{ background: '#FEF3C7', color: '#92400E', padding: '1px 4px', borderRadius: '3px', fontSize: '9px', fontWeight: 700 }}>
                      x{count}
                    </span>
                  </button>
                );
              })
            ) : (
              <span style={{ fontSize: '10.5px', color: '#047857', fontWeight: 600 }}>
                Zero repeated phrases detected ✓
              </span>
            )}
          </div>

          {repeatedItems && repeatedItems.length > 0 && (
            <span style={{ fontSize: '9.5px', color: 'var(--blue)', fontWeight: 500 }}>
              👆 Click any phrase to see transcript dialogue
            </span>
          )}

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {repeatedNote}
          </p>
        </div>

        {/* Tile 4: Talk-to-listen Ratio */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>{t('scorecard.turn_taking_title', undefined, 'Talk-to-listen ratio')}</span>
            {renderBadge(ratioStatus)}
          </div>

          <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--navy)' }}>
            {learnerTalkRatio}% {t('scorecard.learner_share', undefined, 'you')} <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--muted)' }}>· {customerRatio}% {t('scorecard.customer_share', undefined, 'customer')}</span>
          </div>

          {/* Ratio Bar */}
          <div>
            <div style={{ display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden', background: '#E5E7EB' }}>
              {learnerTalkRatio + customerRatio > 0 ? (
                <>
                  <div
                    style={{
                      width: `${learnerTalkRatio}%`,
                      background: ratioStatus === 'Good' ? '#10B981' : '#F59E0B',
                      transition: 'width 0.3s ease'
                    }}
                    title={`You: ${learnerTalkRatio}%`}
                  />
                  <div
                    style={{
                      width: `${customerRatio}%`,
                      background: '#3B82F6',
                      transition: 'width 0.3s ease'
                    }}
                    title={`Customer: ${customerRatio}%`}
                  />
                </>
              ) : (
                <div style={{ width: '100%', background: '#CBD5E1' }} />
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
              <span style={{ fontSize: '9.5px', color: '#10B981', fontWeight: 600 }}>
                ● {t('scorecard.learner_share', undefined, 'You')}: {learnerTalkRatio}%
              </span>
              <span style={{ fontSize: '9.5px', color: 'var(--muted)' }}>
                {t('scorecard.listening_ratio', undefined, 'Ideal: 40–55% balance')}
              </span>
              <span style={{ fontSize: '9.5px', color: '#3B82F6', fontWeight: 600 }}>
                ● {t('scorecard.customer_share', undefined, 'Customer')}: {customerRatio}%
              </span>
            </div>
          </div>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {ratioNote}
          </p>
        </div>

        {/* Tile 5: Interruptions */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>{t('scorecard.interruptions_overlaps', undefined, 'Interruptions')}</span>
            {renderBadge(interruptionStatus)}
          </div>

          <div
            onClick={() => openEvidenceModal('interruption', 'Speech Overlaps', interruptions)}
            style={{
              fontSize: '18px',
              fontWeight: 800,
              color: 'var(--navy)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '6px',
            }}
            title="Click to view speech overlaps and interruption points in transcript"
          >
            <span>{interruptions}</span>
            <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>
              times <span style={{ color: 'var(--blue)', fontSize: '10px', fontWeight: 600 }}>🔍 View</span>
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', color: interruptionStatus === 'Good' ? '#047857' : '#B45309', fontWeight: 600 }}>
            <span>{interruptions === 0 ? 'Zero speech overlaps detected' : `${interruptions} speech overlap(s)`}</span>
            <span>{interruptions === 0 ? '✓' : '•'}</span>
          </div>

          <button
            type="button"
            onClick={() => openEvidenceModal('interruption', 'Speech Overlaps', interruptions)}
            style={{
              border: '1px solid var(--line)',
              background: 'var(--white)',
              color: 'var(--blue)',
              padding: '3px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              width: 'fit-content',
            }}
          >
            <span>🔍 View in transcript</span>
          </button>

          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            Aim for 0 interruptions
          </span>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {interruptionNote}
          </p>
        </div>

        {/* Tile 6: Longest Monologue */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>Longest monologue</span>
            {renderBadge(monologueStatus)}
          </div>

          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--navy)' }}>
            {monologueSec} <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>sec</span>
          </div>

          <div>
            <div style={{ width: '100%', height: '5px', background: '#E5E7EB', borderRadius: '4px', position: 'relative' }}>
              <div style={{ width: `${Math.min(100, (monologueSec / 45) * 100)}%`, height: '100%', background: monologueStatus === 'Good' ? '#10B981' : '#F59E0B', borderRadius: '4px' }} />
            </div>
            <span style={{ fontSize: '9.5px', color: 'var(--muted)', marginTop: '3px', display: 'block' }}>
              Keep under 45 sec
            </span>
          </div>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {monologueNote}
          </p>
        </div>

        {/* Tile 7: Response Time */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>{t('scorecard.response_latency', undefined, 'Response time')}</span>
            {renderBadge(responseStatus)}
          </div>

          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--navy)' }}>
            {responseTime !== null ? responseTime.toFixed(1) : '—'}
            {responseTime !== null && (
              <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}> sec average</span>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: 'var(--muted)', marginBottom: '3px' }}>
              <span>0</span>
              <span style={{ fontWeight: 700, color: responseTime !== null ? '#10B981' : 'var(--faint, #8A94A8)' }}>0.8 - 2.5s Ideal</span>
              <span>4s</span>
            </div>
            <div style={{ width: '100%', height: '5px', background: '#E5E7EB', borderRadius: '4px', position: 'relative' }}>
              <div style={{ position: 'absolute', left: '20%', width: '42.5%', height: '100%', background: '#A7F3D0', borderRadius: '2px' }} />
              {responseTime !== null && (
                <div style={{ position: 'absolute', left: `${Math.min(95, Math.max(5, (responseTime / 4) * 100))}%`, top: '-2px', width: '9px', height: '9px', borderRadius: '50%', background: responseStatus === 'Good' ? '#10B981' : '#F59E0B', border: '1.5px solid white' }} />
              )}
            </div>
          </div>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {responseNote}
          </p>
        </div>

        {/* Tile 8: Questions Asked */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>Questions asked</span>
            {renderBadge(questionStatus)}
          </div>

          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--navy)' }}>
            {questionsCount} <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>({openQuestions} open, {closedQuestions} closed)</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', color: questionStatus === 'Good' ? '#047857' : '#B45309', fontWeight: 600 }}>
            <span>{questionsCount >= 2 ? 'Discovery questions used' : (questionsCount === 1 ? '1 question asked' : 'No questions asked')}</span>
            <span>{questionStatus === 'Good' ? '✓' : '•'}</span>
          </div>

          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            Target: 2+ discovery questions
          </span>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {questionNote}
          </p>
        </div>

        {/* Tile 9: Jargon Used */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px', display: 'grid', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--navy)' }}>Jargon used</span>
            {renderBadge(jargonStatus)}
          </div>

          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--navy)' }}>
            {jargonCount} <span style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--muted)' }}>terms</span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minHeight: '22px' }}>
            {jargonTerms && jargonTerms.length > 0 ? (
              jargonTerms.map((term, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => openEvidenceModal('jargon', term, 1)}
                  style={{
                    fontSize: '9.5px',
                    fontWeight: 600,
                    background: '#FEF3C7',
                    border: '1px solid #FDE68A',
                    color: '#B45309',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                  title={`Click to view "${term}" in transcript dialogue`}
                >
                  <span>{term}</span>
                  <span style={{ opacity: 0.75 }}>🔍</span>
                </button>
              ))
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', color: '#047857', fontWeight: 600 }}>
                <span>No technical jargon detected</span>
                <span>✓</span>
              </div>
            )}
          </div>

          <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.35 }}>
            {jargonNote}
          </p>
        </div>
      </div>

      {/* Evidence Transcript Popup Modal */}
      {activeEvidence && (
        <div
          onClick={() => setActiveEvidence(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(7, 29, 69, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              maxWidth: '640px',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid var(--line)',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--line)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#F8FAFC',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#E8F0FE',
                    color: '#0D4FBD',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '15px',
                  }}
                >
                  💬
                </span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--navy)' }}>
                      {activeEvidence.title}
                    </h3>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        background: activeEvidence.badgeType === 'interruption' ? '#FEE2E2' : '#FEF3C7',
                        color: activeEvidence.badgeType === 'interruption' ? '#991B1B' : '#92400E',
                        padding: '2px 8px',
                        borderRadius: '999px',
                      }}
                    >
                      {activeEvidence.badgeLabel}
                    </span>
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: '11.5px', color: 'var(--muted)' }}>
                    {activeEvidence.description}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveEvidence(null)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  fontSize: '18px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Modal Body: Occurrences List */}
            <div
              style={{
                padding: '16px 20px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: 'calc(85vh - 130px)',
              }}
            >
              {activeEvidence.turns && activeEvidence.turns.length > 0 ? (
                activeEvidence.turns.map((turn, tIdx) => (
                  <div
                    key={tIdx}
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid var(--line)',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      borderLeft: `4px solid ${turn.isLearner ? 'var(--blue)' : '#64748B'}`,
                      display: 'grid',
                      gap: '6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11.5px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontWeight: 700, color: 'var(--navy)' }}>
                          Turn #{turn.turnIndex}
                        </span>
                        <span style={{ color: 'var(--muted)' }}>·</span>
                        <span style={{ fontWeight: 600, color: turn.isLearner ? 'var(--blue)' : '#475569' }}>
                          {turn.speaker}
                        </span>
                      </div>
                      <span style={{ fontSize: '10.5px', color: 'var(--muted)', fontFamily: 'monospace' }}>
                        ⏱️ {turn.timestamp}
                      </span>
                    </div>

                    <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: 'var(--ink)' }}>
                      "{turn.matchedSnippet || turn.text}"
                    </p>
                  </div>
                ))
              ) : (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
                  No verbatim dialogue turns matching this pattern were found in stored transcript records.
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid var(--line)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#F8FAFC',
              }}
            >
              <span style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                Tip: Practicing silent pauses and active listening helps reduce fillers and speech overlaps.
              </span>
              <button
                type="button"
                onClick={() => setActiveEvidence(null)}
                style={{
                  background: 'var(--navy)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 16px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
